/**
 * ImageUpload Component
 * 
 * A reusable component for uploading images for WhatsApp template headers.
 * Follows SOLID principles:
 * - Single Responsibility: Only handles image upload UI and logic
 * - Open/Closed: Easily extensible via props
 * 
 * @module components/ImageUpload
 */
import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

// Types
interface ImageUploadProps {
    /** Callback when upload completes successfully */
    onUploadComplete: (headerHandle: string) => void;
    /** Callback when public URL is ready (for message sending) */
    onPublicUrlReady?: (publicUrl: string) => void;
    /** Callback when image is cleared */
    onClear: () => void;
    /** Callback when preview is ready (for parent to display in phone mockup) */
    onPreviewReady?: (previewUrl: string) => void;
    /** Current header handle (for controlled component) */
    headerHandle?: string;
    /** Preview URL for uploaded image */
    previewUrl?: string;
    /** Whether the component is disabled */
    disabled?: boolean;
    /** Max file size in MB (default 5) */
    maxSizeMB?: number;
}

interface UploadState {
    status: 'idle' | 'uploading' | 'success' | 'error';
    progress: number;
    error?: string;
}

/**
 * ImageUpload - A component for uploading template header images
 */
const ImageUpload: React.FC<ImageUploadProps> = ({
    onUploadComplete,
    onPublicUrlReady,
    onClear,
    onPreviewReady,
    headerHandle,
    previewUrl: externalPreviewUrl,
    disabled = false,
    maxSizeMB = 5,
}) => {
    const [uploadState, setUploadState] = useState<UploadState>({
        status: 'idle',
        progress: 0,
    });
    const [previewUrl, setPreviewUrl] = useState<string>(externalPreviewUrl || '');
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Max size in bytes
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // Supported formats
    const supportedFormats = ['image/jpeg', 'image/png'];

    const validateFile = (file: File): { valid: boolean; error?: string } => {
        // Check format
        if (!supportedFormats.includes(file.type)) {
            return {
                valid: false,
                error: 'Formato no soportado. Solo se permiten JPEG y PNG.',
            };
        }

        // Check size
        if (file.size > maxSizeBytes) {
            const actualSize = (file.size / 1024 / 1024).toFixed(1);
            return {
                valid: false,
                error: `Archivo muy grande: ${actualSize}MB. Máximo ${maxSizeMB}MB.`,
            };
        }

        return { valid: true };
    };

    const uploadFile = async (file: File) => {
        // Validate first
        const validation = validateFile(file);
        if (!validation.valid) {
            setUploadState({
                status: 'error',
                progress: 0,
                error: validation.error,
            });
            toast.error(validation.error);
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            setPreviewUrl(dataUrl);
            // Notify parent about preview for phone mockup
            if (onPreviewReady) {
                onPreviewReady(dataUrl);
            }
        };
        reader.readAsDataURL(file);

        // Start upload
        setUploadState({ status: 'uploading', progress: 0 });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post('/whatsapp/media/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const progress = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 0;
                    setUploadState((prev) => ({ ...prev, progress }));
                },
            });

            // ✅ Success: Meta returns header_handle, Gupshup returns public_url
            if (response.data.success && (response.data.header_handle || response.data.public_url)) {
                setUploadState({ status: 'success', progress: 100 });
                // For Meta: use header_handle. For Gupshup: use public_url as handle fallback
                onUploadComplete(response.data.header_handle || response.data.public_url);
                // ✅ Pass GCS public URL for message sending / preview
                if (response.data.public_url && onPublicUrlReady) {
                    onPublicUrlReady(response.data.public_url);
                }
                toast.success('Imagen subida correctamente');
            } else {
                throw new Error(response.data.error || 'Error al subir imagen');
            }
        } catch (error: any) {
            const errorMsg =
                error.response?.data?.detail ||
                error.message ||
                'Error al subir imagen';
            setUploadState({ status: 'error', progress: 0, error: errorMsg });
            toast.error(errorMsg);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            uploadFile(file);
        }
        // Reset input so same file can be selected again
        e.target.value = '';
    };

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            if (disabled) return;

            const file = e.dataTransfer.files?.[0];
            if (file) {
                uploadFile(file);
            }
        },
        [disabled]
    );

    const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleClear = () => {
        setPreviewUrl('');
        setUploadState({ status: 'idle', progress: 0 });
        onClear();
    };

    const isUploaded = headerHandle || uploadState.status === 'success';

    return (
        <div className="space-y-2">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileSelect}
                className="hidden"
                disabled={disabled}
            />

            {/* Upload Area / Preview */}
            {previewUrl ? (
                // Image Preview
                <div className="relative rounded-lg border border-[#1CB3B3] overflow-hidden bg-gray-50">
                    <img
                        src={previewUrl}
                        alt="Header preview"
                        className="w-full h-32 object-cover"
                    />

                    {/* Overlay with status */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={handleClear}
                            disabled={uploadState.status === 'uploading'}
                            className="shadow-lg"
                        >
                            <X className="w-4 h-4 mr-1" />
                            Quitar
                        </Button>
                    </div>

                    {/* Upload progress indicator */}
                    {uploadState.status === 'uploading' && (
                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                            <Loader2 className="w-8 h-8 text-white animate-spin" />
                            <span className="text-white text-sm mt-2">
                                Subiendo... {uploadState.progress}%
                            </span>
                            <div className="w-3/4 h-1 bg-white/30 rounded-full mt-2 overflow-hidden">
                                <div
                                    className="h-full bg-white transition-all duration-300"
                                    style={{ width: `${uploadState.progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Success indicator */}
                    {isUploaded && uploadState.status !== 'uploading' && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Lista para usar
                        </div>
                    )}
                </div>
            ) : (
                // Upload Drop Zone
                <div
                    onClick={() => !disabled && fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
            transition-all duration-200
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#1CB3B3] hover:bg-[#1CB3B3]/5'}
            ${isDragging ? 'border-[#1CB3B3] bg-[#1CB3B3]/10 scale-[1.02]' : 'border-gray-300'}
            ${uploadState.status === 'error' ? 'border-rose-300 bg-rose-50' : ''}
          `}
                >
                    {uploadState.status === 'uploading' ? (
                        <div className="flex flex-col items-center">
                            <Loader2 className="w-8 h-8 text-[#1CB3B3] animate-spin" />
                            <span className="text-sm text-gray-600 mt-2">
                                Subiendo imagen... {uploadState.progress}%
                            </span>
                        </div>
                    ) : uploadState.status === 'error' ? (
                        <div className="flex flex-col items-center">
                            <AlertCircle className="w-8 h-8 text-rose-500" />
                            <span className="text-sm text-rose-600 mt-2">{uploadState.error}</span>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUploadState({ status: 'idle', progress: 0 });
                                }}
                            >
                                Intentar de nuevo
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-[#E8F7F7] flex items-center justify-center mb-3">
                                <ImageIcon className="w-6 h-6 text-[#1CB3B3]" />
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                                Haz clic o arrastra una imagen
                            </span>
                            <span className="text-xs text-gray-500 mt-1">
                                JPEG o PNG, máximo {maxSizeMB}MB
                            </span>
                            <div className="text-[10px] text-gray-400 mt-2">
                                Dimensión recomendada: 800x418 px
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Info text */}
            <p className="text-[10px] text-gray-400">
                La imagen será subida a Meta y revisada junto con la plantilla.
            </p>
        </div>
    );
};

export default ImageUpload;
